import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import "../css/styles.css";

const HomePage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    business_type: '',
    business_name: '',
    lead_name: '',
    whatsapp_number: '',
    contact_number: '',
    services: [],
    service_location: ''
  });
  const [businessType, setBusinessType] = useState('');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalType, setModalType] = useState('success'); // 'success' or 'error'

  useEffect(() => {
    // FAQ Accordion functionality
    const accordionButtons = document.querySelectorAll('.accordion-button');
    
    const handleAccordionClick = function() {
      const content = this.nextElementSibling;
      
      // Close all other accordions first
      accordionButtons.forEach(otherButton => {
        if (otherButton !== this) {
          otherButton.classList.remove('active');
          otherButton.nextElementSibling.classList.remove('active');
          // Reset icon with a smooth transition
          const icon = otherButton.querySelector('.accordion-icon');
          if (icon) {
            // Remove the active styling first
            setTimeout(() => {
              icon.classList.remove('ri-subtract-line');
              icon.classList.add('ri-add-line');
            }, 50);
          }
        }
      });
      
      // Toggle active class on button and content
      this.classList.toggle('active');
      content.classList.toggle('active');
      
      // Toggle icon
      const icon = this.querySelector('.accordion-icon');
      if (icon) {
        if (this.classList.contains('active')) {
          // Use a smooth transition
          setTimeout(() => {
            icon.classList.remove('ri-add-line');
            icon.classList.add('ri-subtract-line');
          }, 150);
        } else {
          icon.classList.remove('ri-subtract-line');
          icon.classList.add('ri-add-line');
        }
      }
    };
    
    accordionButtons.forEach(button => {
      button.addEventListener('click', handleAccordionClick);
    });


    // Smooth scrolling for anchor links
    const links = document.querySelectorAll('a[href^="#"]');
    
    links.forEach(link => {
      link.addEventListener('click', function(e) {
        e.preventDefault();
        
        const targetId = this.getAttribute('href');
        if (targetId === '#') return;
        
        const targetElement = document.querySelector(targetId);
        if (targetElement) {
          window.scrollTo({
            top: targetElement.offsetTop - 80,
            behavior: 'smooth'
          });
        }
      });
    });

    // Cleanup event listeners on component unmount
    return () => {
      // We can't remove the exact same function reference, but we can remove all click listeners
      // This is a limitation of how we've set up the event listeners
      accordionButtons.forEach(button => {
        const newButton = button.cloneNode(true);
        button.parentNode.replaceChild(newButton, button);
      });
      
      links.forEach(link => {
        const newLink = link.cloneNode(true);
        link.parentNode.replaceChild(newLink, link);
      });
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleBusinessTypeChange = (e) => {
    const value = e.target.value;
    setBusinessType(value);
    setFormData(prev => ({
      ...prev,
      business_type: value
    }));
  };

  const handleServiceChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prev => {
      if (checked) {
        return { ...prev, services: [...prev.services, value] };
      } else {
        return { ...prev, services: prev.services.filter(service => service !== value) };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    // For salon business type, business_name is required
    if (businessType === 'salon' && !formData.business_name) {
      setModalMessage('Please enter your business name');
      setModalType('error');
      setShowModal(true);
      return;
    }
    
    // Validate other required fields
    if (!formData.lead_name || !formData.whatsapp_number || !formData.contact_number || formData.services.length === 0 || !formData.service_location) {
      setModalMessage('Please fill in all required fields');
      setModalType('error');
      setShowModal(true);
      return;
    }

    // Format data for API according to the required payload
    const apiData = {
      type: businessType, // 'salon' or 'freelancer' based on user selection
      lead_name: formData.lead_name,
      business_name: formData.business_name, // Will be empty string for freelancer if not provided
      contact_number: formData.contact_number,
      whatsapp_number: formData.whatsapp_number, // Added whatsapp_number to the payload
      services: formData.services,
      service_location: formData.service_location
    };

    try {
      setLoading(true);
      const response = await fetch('https://partnerapi.ongroom.com/partner/api/v1/leads', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(apiData)
      });

      const data = await response.json();
      
      if (response.ok) {
        // Redirect to thank you page on success
        navigate('/thank-you');
      } else {
        setModalMessage(data.message || 'Something went wrong. Please try again.');
        setModalType('error');
        setShowModal(true);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setModalMessage('Network error. Please check your connection and try again.');
      setModalType('error');
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  // Modal component
  const Modal = ({ message, type, onClose }) => (
    <div className="modal-overlay">
      <div className="modal">
        <button className="modal-close" onClick={onClose}>
          <i className="ri-close-line"></i>
        </button>
        <div className="text-center">
          {type === 'success' ? (
            <div className="success-animation mb-4">
              <div className="checkmark-circle">
                <div className="checkmark draw"></div>
              </div>
            </div>
          ) : (
            <div className="text-red-500 text-5xl mb-4">
              <i className="ri-error-warning-line"></i>
            </div>
          )}
          <h3 className={`text-xl font-semibold mb-2 ${type === 'success' ? '' : 'text-red-600'}`} style={type === 'success' ? {color: '#398c9f'} : {}}>
            {type === 'success' ? 'Success!' : 'Error'}
          </h3>
          <p className="text-gray-700">{message}</p>
        </div>
      </div>
    </div>
  );

  // Loader component
  const Loader = () => (
    <div className="loader-container">
      <div className="loader"></div>
    </div>
  );

  return (
    <>
      {/* Show loader when loading */}
      {loading && <Loader />}

      {/* Show modal when needed */}
      {showModal && (
        <Modal
          message={modalMessage}
          type={modalType}
          onClose={() => setShowModal(false)}
        />
      )}

      {/* Hero Section */}
      <section id="waitlist" className="hero-section relative">
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-white/20"></div>
        <div className="container mx-auto px-6 md:py-10 relative">
          <div className="flex flex-col md:flex-row items-start">
            <div className="w-full md:w-1/2 mb-10 md:mb-0 md:pt-8">
              <h1 className="text-4xl md:text-5xl font-bold text-black mb-10 me-3 leading-tight">
                Grow your Beauty & Grooming Business{" "}
                <span className="text-primary">Digitally</span>
              </h1>
              <p className="text-lg text-gray-700 mb-6">
                Be among the first and get special launch benefits
              </p>
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-10 h-10 flex items-center justify-center bg-primary/10 rounded-full">
                  <i className="ri-check-line text-primary ri-lg"></i>
                </div>
                <p className="text-gray-700">Register For Free</p>
              </div>
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-10 h-10 flex items-center justify-center bg-primary/10 rounded-full">
                  <i className="ri-check-line text-primary ri-lg"></i>
                </div>
                <p className="text-gray-700">
                  Premium features unlocked from day one
                </p>
              </div>
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-10 h-10 flex items-center justify-center bg-primary/10 rounded-full">
                  <i className="ri-check-line text-primary ri-lg"></i>
                </div>
                <p className="text-gray-700">
                  Priority onboarding and dedicated support
                </p>
              </div>
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-10 h-10 flex items-center justify-center bg-primary/10 rounded-full">
                  <i className="ri-check-line text-primary ri-lg"></i>
                </div>
                <p className="text-gray-700">
                  Get discovered by 1000s of new customers
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 flex items-center justify-center bg-primary/10 rounded-full">
                  <i className="ri-check-line text-primary ri-lg"></i>
                </div>
                <p className="text-gray-700">Promote your discounts & offers</p>
              </div>
            </div>

            <div className="w-full md:w-1/2 md:pl-10">
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <h2 className="text-2xl font-semibold text-gray-800 mb-6">
                  Join Our Exclusive Waitlist
                </h2>
                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <label
                      htmlFor="business_type"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Type of Business
                    </label>
                    <div className="relative">
                      <select
                        id="business_type"
                        name="business_type"
                        value={businessType}
                        onChange={handleBusinessTypeChange}
                        className="w-full appearance-none px-4 py-2 pr-8 border-2 border-primary rounded focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary bg-white"
                        required
                      >
                        <option value="" disabled>
                          Select Business Type
                        </option>
                        <option value="salon">Salon</option>
                        <option value="freelancer">Freelancer</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                        <i className="ri-arrow-down-s-line text-gray-500"></i>
                      </div>
                    </div>
                  </div>

                  {businessType === "salon" && (
                    <div className="mb-4">
                      <label
                        htmlFor="business_name"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Business Name
                      </label>
                      <input
                        type="text"
                        id="business_name"
                        name="business_name"
                        value={formData.business_name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border-2 border-primary rounded focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                        placeholder="Your business name"
                        required={businessType === "salon"}
                      />
                    </div>
                  )}

                  <div className="mb-4">
                    <label
                      htmlFor="lead_name"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Owner's Name
                    </label>
                    <input
                      type="text"
                      id="lead_name"
                      name="lead_name"
                      value={formData.lead_name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border-2 border-primary rounded focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                      placeholder="Your full name"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label
                      htmlFor="whatsapp_number"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      WhatsApp Number
                    </label>
                    <input
                      type="tel"
                      id="whatsapp_number"
                      name="whatsapp_number"
                      value={formData.whatsapp_number}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border-2 border-primary rounded focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                      placeholder="Your WhatsApp number"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label
                      htmlFor="contact_number"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="contact_number"
                      name="contact_number"
                      value={formData.contact_number}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border-2 border-primary rounded focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                      placeholder="Your phone number"
                      required
                    />
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Services You Provide
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="hairSalon"
                          name="services"
                          value="hair_salon"
                          checked={formData.services.includes("hair_salon")}
                          onChange={handleServiceChange}
                          className="mr-2 accent-primary border-2 border-primary w-5 h-5"
                        />
                        <label
                          htmlFor="hairSalon"
                          className="text-sm text-gray-600"
                        >
                          Hair Salon
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="nailSalon"
                          name="services"
                          value="nail_salon"
                          checked={formData.services.includes("nail_salon")}
                          onChange={handleServiceChange}
                          className="mr-2 accent-primary border-2 border-primary w-5 h-5"
                        />
                        <label
                          htmlFor="nailSalon"
                          className="text-sm text-gray-600"
                        >
                          Nail Salon
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="spa"
                          name="services"
                          value="spa"
                          checked={formData.services.includes("spa")}
                          onChange={handleServiceChange}
                          className="mr-2 accent-primary border-2 border-primary w-5 h-5"
                        />
                        <label htmlFor="spa" className="text-sm text-gray-600">
                          Spa & Wellness
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="barber"
                          name="services"
                          value="barber"
                          checked={formData.services.includes("barber")}
                          onChange={handleServiceChange}
                          className="mr-2 accent-primary border-2 border-primary w-5 h-5"
                        />
                        <label
                          htmlFor="barber"
                          className="text-sm text-gray-600"
                        >
                          Barbershop
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="facial"
                          name="services"
                          value="facial"
                          checked={formData.services.includes("facial")}
                          onChange={handleServiceChange}
                          className="mr-2 accent-primary border-2 border-primary w-5 h-5"
                        />
                        <label
                          htmlFor="facial"
                          className="text-sm text-gray-600"
                        >
                          Facial Services
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="makeup"
                          name="services"
                          value="makeup"
                          checked={formData.services.includes("makeup")}
                          onChange={handleServiceChange}
                          className="mr-2 accent-primary border-2 border-primary w-5 h-5"
                        />
                        <label
                          htmlFor="makeup"
                          className="text-sm text-gray-600"
                        >
                          Makeup
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="preBridal"
                          name="services"
                          value="pre_bridal"
                          checked={formData.services.includes("pre_bridal")}
                          onChange={handleServiceChange}
                          className="mr-2 accent-primary border-2 border-primary w-5 h-5"
                        />
                        <label
                          htmlFor="preBridal"
                          className="text-sm text-gray-600"
                        >
                          Pre-Bridal
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label
                      htmlFor="service_location"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Service Location
                    </label>
                    <input
                      type="text"
                      id="service_location"
                      name="service_location"
                      value={formData.service_location}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border-2 border-primary rounded focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                      placeholder="Your service location"
                      required
                    />
                    <label className="block text-[12px] font-medium text-gray-500 mb-1">
                      You can paste map link or type the address of your shop or
                      location where you provide service
                    </label>
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-primary text-white py-3 !rounded-button whitespace-nowrap hover:bg-primary-dark transition-colors font-medium"
                    disabled={loading}
                  >
                    {loading ? "Submitting..." : "Join Waitlist"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Early Access Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Early Access Benefits
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Join our waitlist today and unlock these exclusive rewards
              available only for our early partners.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-md border border-gray-100 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 flex items-center justify-center bg-primary/10 rounded-full mb-6">
                <i className="ri-vip-crown-line text-primary ri-2x"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">
                Get Premium Features
              </h3>
              <p className="text-gray-600">
                Get full access to all premium features including advanced
                booking tools, client management, and marketing features at
                discounted rates for 1st 6 months.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md border border-gray-100 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 flex items-center justify-center bg-primary/10 rounded-full mb-6">
                <i className="ri-percent-line text-primary ri-2x"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">
                Zero Commission Forever
              </h3>
              <p className="text-gray-600">
                Keep 100% of your earnings forever. No platform fees or
                commission charges on any bookings.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md border border-gray-100 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 flex items-center justify-center bg-primary/10 rounded-full mb-6">
                <i className="ri-customer-service-line text-primary ri-2x"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">
                Priority Support & Setup Assistance(Forever)
              </h3>
              <p className="text-gray-600">
                Receive dedicated onboarding support with a personal account
                manager to help you set up your profile and maximize your
                business potential.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md border border-gray-100 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 flex items-center justify-center bg-primary/10 rounded-full mb-6">
                <i className="ri-store-line text-primary ri-2x"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">
                List your business and your services (Forever Free)
              </h3>
              <p className="text-gray-600">
                Let 1000s of customers discover you and your services without
                you spending a penny.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Future Features Preview */}
      <section id="features" className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Powerful Features Coming Soon
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Transform how you connect with clients and grow your beauty
              business with these game-changing features.
            </p>
          </div>

          <div className="space-y-20">
            {/* Feature 1 */}
            <div className="flex flex-col md:flex-row items-center">
              <div className="w-full md:w-1/2 mb-8 md:mb-0 md:pr-10">
                <h3 className="text-2xl font-semibold text-gray-800 mb-4">
                  Meet Clients Faster
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <div className="w-6 h-6 flex items-center justify-center bg-primary/10 rounded-full mt-0.5 mr-3">
                      <i className="ri-check-line text-primary ri-sm"></i>
                    </div>
                    <p className="text-gray-700">
                      Let your potential customers find your business and your
                      services
                    </p>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 flex items-center justify-center bg-primary/10 rounded-full mt-0.5 mr-3">
                      <i className="ri-check-line text-primary ri-sm"></i>
                    </div>
                    <p className="text-gray-700">
                      Customers can see your available time slots and hence book
                      from the comfort of their home.
                    </p>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 flex items-center justify-center bg-primary/10 rounded-full mt-0.5 mr-3">
                      <i className="ri-check-line text-primary ri-sm"></i>
                    </div>
                    <p className="text-gray-700">
                      Showcase your offers & Discounts to your customers with
                      very-very nominal charges without depending upon Facebook
                      and Google ads.
                    </p>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 flex items-center justify-center bg-primary/10 rounded-full mt-0.5 mr-3">
                      <i className="ri-check-line text-primary ri-sm"></i>
                    </div>
                    <p className="text-gray-700">
                      Let your customers schedule your services in advance
                    </p>
                  </li>
                </ul>
              </div>
              <div className="w-full md:w-1/2">
                <div className="rounded-lg overflow-hidden shadow-lg">
                  <img
                    src="/img/clients.jpg"
                    alt="Fast Client Connection Interface"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="flex flex-col md:flex-row-reverse items-center">
              <div className="w-full md:w-1/2 mb-8 md:mb-0 md:pl-10">
                <h3 className="text-2xl font-semibold text-gray-800 mb-4">
                  Take orders as per your choice
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <div className="w-6 h-6 flex items-center justify-center bg-primary/10 rounded-full mt-0.5 mr-3">
                      <i className="ri-check-line text-primary ri-sm"></i>
                    </div>
                    <p className="text-gray-700">
                      Accept or decline orders as per your choice
                    </p>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 flex items-center justify-center bg-primary/10 rounded-full mt-0.5 mr-3">
                      <i className="ri-check-line text-primary ri-sm"></i>
                    </div>
                    <p className="text-gray-700">
                      Switch ON/OFF the automatic order acceptance feature,
                      which will allow to get all the orders accepted
                      automatically.
                    </p>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 flex items-center justify-center bg-primary/10 rounded-full mt-0.5 mr-3">
                      <i className="ri-check-line text-primary ri-sm"></i>
                    </div>
                    <p className="text-gray-700">
                      Switch ON/OFF your OR your team member's availability any
                      time
                    </p>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 flex items-center justify-center bg-primary/10 rounded-full mt-0.5 mr-3">
                      <i className="ri-check-line text-primary ri-sm"></i>
                    </div>
                    <p className="text-gray-700">
                      Get a dashboard showing your orders and available time
                      slots.
                    </p>
                  </li>
                </ul>
              </div>
              <div className="w-full md:w-1/2">
                <div className="rounded-lg overflow-hidden shadow-lg">
                  <img
                    src="/img/choose-client.jpg"
                    alt="Flexible Order Management Interface"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="flex flex-col md:flex-row items-center">
              <div className="w-full md:w-1/2 mb-8 md:mb-0 md:pr-10">
                <h3 className="text-2xl font-semibold text-gray-800 mb-4">
                  Get 5X More Business
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <div className="w-6 h-6 flex items-center justify-center bg-primary/10 rounded-full mt-0.5 mr-3">
                      <i className="ri-check-line text-primary ri-sm"></i>
                    </div>
                    <p className="text-gray-700">
                      AI-powered promotion to thousands of potential clients
                    </p>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 flex items-center justify-center bg-primary/10 rounded-full mt-0.5 mr-3">
                      <i className="ri-check-line text-primary ri-sm"></i>
                    </div>
                    <p className="text-gray-700">
                      Featured placement during peak booking hours
                    </p>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 flex items-center justify-center bg-primary/10 rounded-full mt-0.5 mr-3">
                      <i className="ri-check-line text-primary ri-sm"></i>
                    </div>
                    <p className="text-gray-700">
                      Earn more than your basic price for each service
                    </p>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 flex items-center justify-center bg-primary/10 rounded-full mt-0.5 mr-3">
                      <i className="ri-check-line text-primary ri-sm"></i>
                    </div>
                    <p className="text-gray-700">Retain your customers</p>
                  </li>
                </ul>
              </div>
              <div className="w-full md:w-1/2">
                <div className="rounded-lg overflow-hidden shadow-lg">
                  <img
                    src="/img/grow-business.jpg"
                    alt="Business Growth Analytics"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Feature 4 */}
            <div className="flex flex-col md:flex-row items-center mt-16">
              <div className="w-full md:w-1/2">
                <div className="rounded-lg overflow-hidden shadow-lg">
                  <img
                    src="/img/interaction.jpg"
                    alt="Seamless Customer Interaction"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
              <div className="w-full md:w-1/2 mb-8 md:mb-0 md:pl-10">
                <h3 className="text-2xl font-semibold text-gray-800 mb-4">
                  Seamless Customer Interaction
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <div className="w-6 h-6 flex items-center justify-center bg-primary/10 rounded-full mt-0.5 mr-3">
                      <i className="ri-check-line text-primary ri-sm"></i>
                    </div>
                    <p className="text-gray-700">Smart time-slot management</p>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 flex items-center justify-center bg-primary/10 rounded-full mt-0.5 mr-3">
                      <i className="ri-check-line text-primary ri-sm"></i>
                    </div>
                    <p className="text-gray-700">
                      Real customer reviews and feedbacks
                    </p>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 flex items-center justify-center bg-primary/10 rounded-full mt-0.5 mr-3">
                      <i className="ri-check-line text-primary ri-sm"></i>
                    </div>
                    <p className="text-gray-700">
                      Secure payments, direct to your bank accounts
                    </p>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 flex items-center justify-center bg-primary/10 rounded-full mt-0.5 mr-3">
                      <i className="ri-check-line text-primary ri-sm"></i>
                    </div>
                    <p className="text-gray-700">
                      Real-time chat availability with you and your customers
                    </p>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Exclusive Launch Rewards */}
      <section id="rewards" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Exclusive Launch Rewards
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The earlier you join, the more benefits you'll receive. Secure
              your spot now to unlock these special rewards.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {/* Milestone 1 */}
            <div className="relative pl-8 md:pl-0">
              <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 border-l-2 border-primary/30 h-full z-0"></div>

              <div className="flex flex-col md:flex-row md:items-center mb-12 md:mb-16 relative">
                <div className="md:w-1/2 md:pr-12 md:text-right mb-6 md:mb-0">
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    First 100 Partners
                  </h3>
                  <p className="text-gray-600">
                    Join the exclusive founding members group with the most
                    premium benefits.
                  </p>
                </div>

                <div className="hidden md:flex absolute left-1/2 transform -translate-x-1/2 w-12 h-12 bg-primary text-white rounded-full items-center justify-center z-10">
                  <span className="font-semibold">1</span>
                </div>

                <div className="md:w-1/2 md:pl-12 bg-white p-6 rounded-lg shadow-md border border-gray-100">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary/10 rounded-full mr-4">
                      <i className="ri-camera-line text-primary ri-lg"></i>
                    </div>
                    <h4 className="font-semibold text-gray-800">
                      Professional Photoshoot
                    </h4>
                  </div>
                  <p className="text-gray-600 mb-3">
                    A complimentary professional photoshoot for your business to
                    showcase your services and space.
                  </p>
                  <div className="bg-gray-100 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-primary h-full"
                      style={{ width: "15%" }}
                    ></div>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    43 spots remaining
                  </p>
                </div>
              </div>

              {/* Milestone 2 */}
              <div className="flex flex-col md:flex-row md:items-center mb-12 md:mb-16 relative">
                <div className="md:w-1/2 md:pr-12 md:text-right order-1 md:order-1 mb-6 md:mb-0">
                  <div className="md:hidden w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center mb-4">
                    <span className="font-semibold">2</span>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                    <div className="flex items-center mb-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-primary/10 rounded-full mr-4">
                        <i className="ri-medal-line text-primary ri-lg"></i>
                      </div>
                      <h4 className="font-semibold text-gray-800">
                        Featured Placement
                      </h4>
                    </div>
                    <p className="text-gray-600 mb-3">
                      Get premium placement in search results and featured
                      section for the first 3 months after launch.
                    </p>
                    <div className="bg-gray-100 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-primary h-full"
                        style={{ width: "30%" }}
                      ></div>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                      70 spots remaining
                    </p>
                  </div>
                </div>

                <div className="hidden md:flex absolute left-1/2 transform -translate-x-1/2 w-12 h-12 bg-primary text-white rounded-full items-center justify-center z-10">
                  <span className="font-semibold">2</span>
                </div>

                <div className="md:w-1/2 md:pl-12 order-2 md:order-2">
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    First 100 Partners
                  </h3>
                  <p className="text-gray-600">
                    Early adopters with significant competitive advantages.
                  </p>
                </div>
              </div>

              {/* Milestone 3 */}
              <div className="flex flex-col md:flex-row md:items-center relative">
                <div className="md:w-1/2 md:pr-12 md:text-right mb-6 md:mb-0">
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    First 200 Partners
                  </h3>
                  <p className="text-gray-600">
                    Join before the public launch and still receive special
                    benefits.
                  </p>
                </div>

                <div className="hidden md:flex absolute left-1/2 transform -translate-x-1/2 w-12 h-12 bg-primary text-white rounded-full items-center justify-center z-10">
                  <span className="font-semibold">3</span>
                </div>

                <div className="md:w-1/2 md:pl-12 bg-white p-6 rounded-lg shadow-md border border-gray-100">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary/10 rounded-full mr-4">
                      <i className="ri-advertisement-line text-primary ri-lg"></i>
                    </div>
                    <h4 className="font-semibold text-gray-800">
                      Reach Thousands of Customers FREE
                    </h4>
                  </div>
                  <p className="text-gray-600 mb-3">
                    Reach your potential customers Forever Free. Also, get
                    highly discounted premium features for the first 6 months.
                  </p>
                  <div className="bg-gray-100 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-primary h-full"
                      style={{ width: "5%" }}
                    ></div>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    190 spots remaining
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Getting started with OnGroom is simple. Follow these steps to
              secure your spot.
            </p>
          </div>

          <div className="flex flex-col md:flex-row justify-center items-center md:items-start gap-8 md:gap-16">
            <div className="max-w-xs text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-primary text-white text-2xl font-bold rounded-full mx-auto mb-6">
                1
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">
                Join the Waitlist
              </h3>
              <p className="text-gray-600">
                Fill out the simple form with your business details to secure
                your position in our exclusive waitlist.
              </p>
            </div>

            <div className="max-w-xs text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-primary text-white text-2xl font-bold rounded-full mx-auto mb-6">
                2
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">
                Get Launch Updates
              </h3>
              <p className="text-gray-600">
                Stay informed with regular updates about our progress, features,
                and your special rewards.
              </p>
            </div>

            <div className="max-w-xs text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-primary text-white text-2xl font-bold rounded-full mx-auto mb-6">
                3
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">
                Early Access & Rewards
              </h3>
              <p className="text-gray-600">
                Receive priority access when we launch and unlock all your
                exclusive benefits and rewards.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call-to-Action Section */}
      <section className="py-20 bg-primary text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Transform Your Beauty Business?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Join our waitlist today and be among the first to experience the
            future of beauty service management.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 max-w-4xl mx-auto mb-10">
            <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
              <i className="ri-check-line text-2xl mb-2"></i>
              <p className="text-sm font-medium">Register For Free</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
              <i className="ri-vip-crown-line text-2xl mb-2"></i>
              <p className="text-sm font-medium">Premium features unlocked</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
              <i className="ri-customer-service-line text-2xl mb-2"></i>
              <p className="text-sm font-medium">Priority onboarding</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
              <i className="ri-user-search-line text-2xl mb-2"></i>
              <p className="text-sm font-medium">Get discovered by 1000s</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
              <i className="ri-coupon-line text-2xl mb-2"></i>
              <p className="text-sm font-medium">Promote your offers</p>
            </div>
          </div>

          <a
            href="#waitlist"
            className="inline-block bg-white text-primary font-semibold px-8 py-4 rounded-lg shadow-lg hover:bg-gray-50 hover:text-primary-dark transition-colors"
          >
            Join Waitlist Now
          </a>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-gray-100">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Everything you need to know about becoming an OnGroom partner
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-6">
            <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow bg-white">
              <button
                className="accordion-button w-full flex items-center justify-between p-6 text-left transition-colors"
                id="faq-1-button"
                aria-expanded="false"
                aria-controls="faq-1-content"
              >
                <div className="flex items-center">
                  <div className="w-8 h-8 rounded-full bg-primary-light flex items-center justify-center mr-4">
                    <span className="text-primary font-semibold">1</span>
                  </div>
                  <span className="text-lg font-medium text-gray-800">
                    When will OnGroom officially launch?
                  </span>
                </div>
                <i className="accordion-icon ri-add-line text-primary text-xl"></i>
              </button>
              <div className="accordion-content p-6 border-t border-gray-200 bg-white">
                <p className="text-gray-600 pl-12">
                  We're planning to launch in Q1 2026. Early waitlist members
                  will receive access before the public launch, with the first
                  50 partners getting access approximately 1 month before the
                  official launch date.
                </p>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow bg-white">
              <button
                className="accordion-button w-full flex items-center justify-between p-6 text-left transition-colors"
                id="faq-2-button"
                aria-expanded="false"
                aria-controls="faq-2-content"
              >
                <div className="flex items-center">
                  <div className="w-8 h-8 rounded-full bg-primary-light flex items-center justify-center mr-4">
                    <span className="text-primary font-semibold">2</span>
                  </div>
                  <span className="text-lg font-medium text-gray-800">
                    Can I still serve my offline clients alongside OnGroom?
                  </span>
                </div>
                <i className="accordion-icon ri-add-line text-primary text-xl"></i>
              </button>
              <div className="accordion-content p-6 border-t border-gray-200 bg-white">
                <p className="text-gray-600 pl-12">
                  Absolutely! OnGroom is designed to complement your existing
                  business. You'll have all the flexibility and convenience of
                  running your own business, while still benefiting from our
                  platform's features and services. You can manage both online
                  and offline clients through our unified dashboard.
                </p>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow bg-white">
              <button
                className="accordion-button w-full flex items-center justify-between p-6 text-left transition-colors"
                id="faq-3-button"
                aria-expanded="false"
                aria-controls="faq-3-content"
              >
                <div className="flex items-center">
                  <div className="w-8 h-8 rounded-full bg-primary-light flex items-center justify-center mr-4">
                    <span className="text-primary font-semibold">3</span>
                  </div>
                  <span className="text-lg font-medium text-gray-800">
                    Will there be a mobile app for partners?
                  </span>
                </div>
                <i className="accordion-icon ri-add-line text-primary text-xl"></i>
              </button>
              <div className="accordion-content p-6 border-t border-gray-200 bg-white">
                <p className="text-gray-600 pl-12">
                  Yes, we're developing both iOS and Android apps for service
                  providers to manage their business on the go. The apps will
                  include all the features of the web platform, plus
                  mobile-specific features like push notifications for new
                  bookings and client messages. Early partners will have access
                  to beta versions of these apps and can provide feedback to
                  shape the final product.
                </p>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow bg-white">
              <button
                className="accordion-button w-full flex items-center justify-between p-6 text-left transition-colors"
                id="faq-4-button"
                aria-expanded="false"
                aria-controls="faq-4-content"
              >
                <div className="flex items-center">
                  <div className="w-8 h-8 rounded-full bg-primary-light flex items-center justify-center mr-4">
                    <span className="text-primary font-semibold">4</span>
                  </div>
                  <span className="text-lg font-medium text-gray-800">
                    How will clients find my business on the platform?
                  </span>
                </div>
                <i className="accordion-icon ri-add-line text-primary text-xl"></i>
              </button>
              <div className="accordion-content p-6 border-t border-gray-200 bg-white">
                <p className="text-gray-600 pl-12">
                  Clients will discover your business through our powerful
                  search and discovery features, which include:
                </p>
                <ul className="list-disc pl-16 mt-3 text-gray-600 space-y-2">
                  <li>Location-based search with map integration</li>
                  <li>Service category filtering and specialized searches</li>
                  <li>
                    Personalized recommendations based on client preferences
                  </li>
                  <li>
                    Featured placement for early partners in search results
                  </li>
                  <li>
                    Promotional campaigns to drive initial client acquisition
                  </li>
                </ul>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow bg-white">
              <button
                className="accordion-button w-full flex items-center justify-between p-6 text-left transition-colors"
                id="faq-5-button"
                aria-expanded="false"
                aria-controls="faq-5-content"
              >
                <div className="flex items-center">
                  <div className="w-8 h-8 rounded-full bg-primary-light flex items-center justify-center mr-4">
                    <span className="text-primary font-semibold">5</span>
                  </div>
                  <span className="text-lg font-medium text-gray-800">
                    What fees or commissions will OnGroom charge?
                  </span>
                </div>
                <i className="accordion-icon ri-add-line text-primary text-xl"></i>
              </button>
              <div className="accordion-content p-6 border-t border-gray-200 bg-white">
                <p className="text-gray-600 pl-12">
                  OnGroom offers transparent pricing with no hidden fees. Early
                  partners will benefit from our most competitive rates:
                </p>
                <ul className="list-disc pl-16 mt-3 text-gray-600 space-y-2">
                  <li>
                    A small percentage fee on bookings made through the platform
                    (significantly lower for early partners)
                  </li>
                  <li>No monthly subscription fees for basic features</li>
                  <li>
                    Optional premium features available for a small monthly fee
                  </li>
                  <li>
                    Early partners receive premium features free for the first 6
                    months
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;